import {
  ActionIcon,
  Alert,
  Button,
  Group,
  Modal,
  NumberInput,
  Paper,
  SegmentedControl,
  Select,
  SimpleGrid,
  Stack,
  Table,
  Text,
  TextInput,
} from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { IconArrowLeft, IconPencil, IconPlus, IconSearch, IconTrash } from '@tabler/icons-react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router';
import { NapakaApi, sporocilo } from '../api/napaka';
import { api, podatki } from '../api/odjemalec';
import { denar, odstotek } from '../oblika/oblika';
import { StanjeNalaganja } from '../postavitev/Podatki';
import { GlavaZaslona, ZaslonSeznama } from '../seznam/Seznam';
import { obrezano, VRSTE_POSTAVK, type VrstaPostavke } from './delavnica';
import type { PostavkaCenika } from './IzbiraCenika';
import { usePravica } from '../seja/pravice';

/**
 * Cenik delavnice: šifrant dela, materiala in nadomestnih delov za postavke naloga (cena brez DDV, enota, DDV –
 * prazno = stopnja podjetja). Dodajanje in urejanje v oknu, »Odstrani« postavko skrije (nalogi jo obdržijo). Ročno
 * vpisane nove postavke na nalogu se sem dodajo same.
 */
export function CenikDelavnice() {
  const [vrsta, setVrsta] = useState<'VSE' | VrstaPostavke>('VSE');
  const [iskanje, setIskanje] = useState('');
  const [zakasnjeno] = useDebouncedValue(iskanje, 250);
  const [urejana, setUrejana] = useState<PostavkaCenika | null>(null);
  const odjemalec = useQueryClient();
  const cenik = useQuery({
    queryKey: ['cenik-delavnice', vrsta === 'VSE' ? undefined : vrsta, zakasnjeno],
    queryFn: ({ signal }) =>
      podatki(
        api.GET('/api/delavnica/cenik', {
          signal,
          params: { query: { vrsta: vrsta === 'VSE' ? undefined : vrsta, iskanje: zakasnjeno || undefined } },
        }),
      ),
    placeholderData: keepPreviousData,
  });
  const odstrani = useMutation({
    mutationFn: (id: number) => podatki(api.DELETE('/api/delavnica/cenik/{id}', { params: { path: { id } } })),
    onSuccess: async () => {
      notifications.show({ color: 'green', message: 'Postavka je odstranjena iz cenika.' });
      await odjemalec.invalidateQueries({ queryKey: ['cenik-delavnice'] });
    },
  });
  const vrstaNaziv = (v?: string) => VRSTE_POSTAVK.find((x) => x.value === v)?.label ?? '';

  return (
    <ZaslonSeznama>
      <Button
        component={Link}
        to="/delavnica/nalogi"
        variant="subtle"
        leftSection={<IconArrowLeft size={16} />}
        mb="xs"
        px={0}
      >
        Delovni nalogi
      </Button>
      <GlavaZaslona
        naslov="Cenik delavnice"
        opis="Delo, material in nadomestni deli za postavke nalogov."
        pomoc="delovni-nalogi"
        dejanja={
          <Button
            leftSection={<IconPlus size={18} />}
            onClick={() => setUrejana({ vrsta: 'DELO', enota: 'h', aktivno: true })}
          >
            Nova postavka
          </Button>
        }
      />
      <Paper radius="lg" p="md" shadow="xs">
        <Stack gap="sm">
          <Group gap="sm" wrap="wrap">
            <TextInput
              aria-label="Iskanje"
              placeholder="Išči po nazivu ali šifri"
              leftSection={<IconSearch size={16} />}
              value={iskanje}
              onChange={(e) => setIskanje(e.currentTarget.value)}
              style={{ flex: '1 1 240px' }}
            />
            <SegmentedControl
              data={[{ value: 'VSE', label: 'Vse' }, ...VRSTE_POSTAVK]}
              value={vrsta}
              onChange={(v) => setVrsta(v as 'VSE' | VrstaPostavke)}
            />
          </Group>
          {cenik.isError ? (
            <StanjeNalaganja
              napaka={cenik.error}
              ponovi={() => void cenik.refetch()}
              naslov="Cenika ni bilo mogoče naložiti"
            />
          ) : (cenik.data ?? []).length === 0 ? (
            <Text size="sm" c="dimmed" py="md">
              {cenik.isPending ? 'Nalagam …' : 'V ceniku ni postavk za izbrane pogoje.'}
            </Text>
          ) : (
            <Table.ScrollContainer minWidth={680}>
              <Table striped highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Vrsta</Table.Th>
                    <Table.Th>Naziv</Table.Th>
                    <Table.Th>Šifra</Table.Th>
                    <Table.Th>Enota</Table.Th>
                    <Table.Th ta="right">Cena brez DDV</Table.Th>
                    <Table.Th ta="right">DDV</Table.Th>
                    <Table.Th />
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {(cenik.data ?? []).map((p) => (
                    <Table.Tr key={p.id}>
                      <Table.Td>{vrstaNaziv(p.vrsta)}</Table.Td>
                      <Table.Td fw={600}>{p.naziv}</Table.Td>
                      <Table.Td>{p.sifra}</Table.Td>
                      <Table.Td>{p.enota}</Table.Td>
                      <Table.Td ta="right" className="brez-preloma">
                        {denar(p.cena)}
                      </Table.Td>
                      <Table.Td ta="right" className="brez-preloma">
                        {p.ddvStopnja != null ? `${odstotek(p.ddvStopnja)} %` : 'podjetja'}
                      </Table.Td>
                      <Table.Td ta="right" className="brez-preloma">
                        <ActionIcon variant="subtle" aria-label={`Uredi ${p.naziv}`} onClick={() => setUrejana(p)}>
                          <IconPencil size={18} />
                        </ActionIcon>
                        <ActionIcon
                          variant="subtle"
                          color="red"
                          aria-label={`Odstrani ${p.naziv}`}
                          onClick={() => p.id && odstrani.mutate(p.id)}
                          loading={odstrani.isPending && odstrani.variables === p.id}
                        >
                          <IconTrash size={18} />
                        </ActionIcon>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
          )}
          {odstrani.error && (
            <Alert color="red" variant="light" role="alert">
              {sporocilo(odstrani.error)}
            </Alert>
          )}
        </Stack>
      </Paper>
      <Modal
        opened={!!urejana}
        onClose={() => setUrejana(null)}
        title={urejana?.id ? 'Uredi postavko cenika' : 'Nova postavka cenika'}
      >
        {urejana && <ObrazecCenika key={urejana.id ?? 'nova'} zacetek={urejana} zapri={() => setUrejana(null)} />}
      </Modal>
    </ZaslonSeznama>
  );
}

function ObrazecCenika({ zacetek, zapri }: { zacetek: PostavkaCenika; zapri: () => void }) {
  // nabavne cene: brez pregleda skrite, brez urejanja samo za ogled (strežnik jih ohrani)
  const nabavne = usePravica('NABAVNE_CENE');
  const urejanjeNabavnih = usePravica('NABAVNE_CENE', 'UREJANJE');
  const odjemalec = useQueryClient();
  const [p, setP] = useState(zacetek);
  const [polja, setPolja] = useState<Record<string, string>>({});
  const shrani = useMutation({
    mutationFn: () => {
      const body = {
        ...p,
        naziv: obrezano(p.naziv),
        sifra: obrezano(p.sifra),
        enota: obrezano(p.enota),
        dobavitelj: obrezano(p.dobavitelj),
      };
      return p.id
        ? podatki(api.PUT('/api/delavnica/cenik/{id}', { params: { path: { id: p.id } }, body }))
        : podatki(api.POST('/api/delavnica/cenik', { body }));
    },
    onMutate: () => setPolja({}),
    onSuccess: async () => {
      await odjemalec.invalidateQueries({ queryKey: ['cenik-delavnice'] });
      zapri();
    },
    onError: (e) => e instanceof NapakaApi && setPolja(e.polja),
  });
  const decimalno = { decimalSeparator: ',', allowNegative: false, decimalScale: 2 } as const;
  const besedilo = (k: 'naziv' | 'sifra' | 'enota' | 'dobavitelj', oznaka: string) => (
    <TextInput
      label={oznaka}
      value={p[k] ?? ''}
      onChange={(e) => {
        const v = e.currentTarget.value;
        setP((x) => ({ ...x, [k]: v }));
      }}
      onBlur={() => setP((x) => ({ ...x, [k]: obrezano(x[k]) }))}
      error={polja[k]}
      withAsterisk={k === 'naziv'}
    />
  );
  return (
    <Stack gap="sm">
      <Select
        label="Vrsta"
        data={VRSTE_POSTAVK}
        value={p.vrsta ?? null}
        onChange={(v) => setP((x) => ({ ...x, vrsta: (v as VrstaPostavke) ?? undefined }))}
        allowDeselect={false}
        error={polja.vrsta}
      />
      {besedilo('naziv', 'Naziv')}
      <SimpleGrid cols={2} spacing="sm">
        {besedilo('sifra', 'Šifra')}
        {besedilo('enota', 'Enota')}
        <NumberInput
          label="Cena brez DDV"
          value={p.cena ?? ''}
          onChange={(v) => setP((x) => ({ ...x, cena: typeof v === 'number' ? v : undefined }))}
          error={polja.cena}
          thousandSeparator="."
          rightSection="€"
          {...decimalno}
        />
        <NumberInput
          label="DDV"
          description="Prazno = stopnja podjetja"
          value={p.ddvStopnja ?? ''}
          onChange={(v) => setP((x) => ({ ...x, ddvStopnja: typeof v === 'number' ? v : undefined }))}
          error={polja.ddvStopnja}
          max={100}
          rightSection="%"
          {...decimalno}
        />
      </SimpleGrid>
      {p.vrsta !== 'DELO' && (
        <SimpleGrid cols={2} spacing="sm">
          {besedilo('dobavitelj', 'Dobavitelj')}
          {nabavne && (
            <NumberInput
              label="Nabavna cena"
              disabled={!urejanjeNabavnih}
              value={p.nabavnaCena ?? ''}
              onChange={(v) => setP((x) => ({ ...x, nabavnaCena: typeof v === 'number' ? v : undefined }))}
              error={polja.nabavnaCena}
              thousandSeparator="."
              rightSection="€"
              {...decimalno}
            />
          )}
        </SimpleGrid>
      )}
      {shrani.error && !(shrani.error instanceof NapakaApi && Object.keys(shrani.error.polja).length > 0) && (
        <Alert color="red" variant="light" role="alert">
          {sporocilo(shrani.error)}
        </Alert>
      )}
      <Group justify="flex-end" gap="xs">
        <Button variant="subtle" onClick={zapri} disabled={shrani.isPending}>
          Prekliči
        </Button>
        <Button onClick={() => shrani.mutate()} loading={shrani.isPending}>
          Shrani
        </Button>
      </Group>
    </Stack>
  );
}
